import Vue from "vue";
import Router from "vue-router";
import lodash from "lodash";
import i18n from "@/i18n";

import Organizations from "@pages/organizations/Organizations";
import OrganizationUpdate from "@pages/organizations/OrganizationUpdate";
import LeadMasks from "@pages/lead_masks/LeadMasks";
import LeadMaskUpdate from "@pages/lead_masks/LeadMaskUpdate";
import Users from "@pages/users/Users";
import UserUpdate from "@pages/users/UserUpdate";
import UserProfile from "@pages/users/UserProfile";
import ExternalForms from "@pages/external_forms/ExternalForms";
import ExternalFormUpdate from "@pages/external_forms/ExternalFormUpdate";
import ExternalFormEditor from "@pages/external_forms/ExternalFormEditor";
import ExternalFormPreview from "@pages/external_forms/ExternalFormPreview";
import ExternalFormOptions from "@pages/external_forms/ExternalFormOptions";
import Projects from "@pages/projects/Projects";
import ProjectsSynchronize from "@pages/projects/ProjectsSynchronize";
import ProjectAssignments from "@pages/projects/ProjectAssignments";
import ProjectUpdate from "@pages/projects/ProjectUpdate";
import ProjectWizard from "@pages/projects/ProjectWizard";
import ProjectUpdateAssignement from "@pages/projects/ProjectUpdateAssignement";
import Statistics from "@pages/statistics/Statistics";
import Contacts from "@pages/contacts/Contacts";
import Companies from "@pages/companies/Companies";
import Company from "@pages/companies/Company";
import Segments from "@pages/segments/Segments";
import LeadMaskEditor from "@pages/lead_masks/LeadMaskEditor";
import LeadMaskOptions from "@pages/lead_masks/LeadMaskOptions";
import SegmentEdit from "@pages/segments/Segment";
import Contact from "@pages/contacts/Contact";
import ProjectLeads from "@pages/project/leads/Leads";
import ProjectLeadUpdate from "@pages/project/leads/LeadUpdate";
import ProjectLeadDetailedStatistics from "@pages/project/leads/LeadDetailedStatistics";
import ProjectLeadDetailedStatisticsFrame from "@pages/project/leads/LeadDetailedStatisticsFrame";
import ProjectLeadCreate from "@pages/project/leads/LeadCreate";
import ProjectStatistics from "@pages/projects/ProjectStatistics";
import ProjectStatisticsTrackings from "@pages/projects/ProjectTrackings";
import ProjectStatisticsTrackingsFrame from "@pages/projects/ProjectTrackingsFrame";
import EmailTemplates from "@pages/email_templates/EmailTemplates";
import Dashboard from "@pages/dashboard/Dashboard";
import EmailTemplateUpdate from "@pages/email_templates/EmailTemplateUpdate";
import ContentManager from "@pages/content_manager/ContentManager";
import Login from "@/components/auth/Login";
// import Segment from "@/components/pages/Segment";
import PasswordResetRequest from "@/components/auth/PasswordResetRequest";
import PasswordReset from "@/components/auth/PasswordReset";
import Invitation from "@/components/auth/Invitation";
import Unauthorized from "@pages/Unauthorized";
import PlanUpdate from "@/components/pages/plans/PlanUpdate";
import Plans from "@/components/pages/plans/Plans";
import Workflows from "@/components/pages/workflows/Workflows";
import WorkflowUpdate from "@/components/pages/workflows/WorkflowUpdate";

import Meta from "vue-meta";

let router = new Router({
  mode: "history",
  routes: [
    {
      path: "/login",
      name: "login",
      component: Login,
      meta: {
        layout: "public"
      }
    },
    {
      path: "/password_reset_request",
      name: "password_reset_request",
      component: PasswordResetRequest,
      meta: {
        layout: "public"
      }
    },
    {
      path: "/reset_password/:token",
      name: "password_reset",
      component: PasswordReset,
      meta: {
        layout: "public"
      }
    },
    {
      path: "/accept_invitation/:token",
      name: "accept_invitation",
      component: Invitation,
      public: true,
      meta: {
        layout: "public"
      }
    },
    {
      path: "/logout",
      name: "logout",
      redirect: {
        name: "login",
        query: {
          source: "logout"
        }
      }
    },
    {
      path: "/",
      name: "home",
      redirect: { name: "dashboard" }
    },
    {
      path: "/ext-forms",
      name: "external_forms",
      component: ExternalForms,
      meta: {
        layout: "classic"
      }
    },
    {
      path: "/ext-form/:id/",
      name: "external_form_edit",
      component: ExternalFormUpdate,
      props: true,
      meta: {
        layout: "classic"
      },
      children: [
        {
          path: "edit",
          name: "external_form_editor",
          component: ExternalFormEditor,
          meta: {
            externalFormEdit: true
          }
        },
        {
          path: "preview",
          name: "external_form_preview",
          component: ExternalFormPreview,
          meta: {
            externalFormEdit: true
          }
        },
        {
          path: "options",
          name: "external_form_options",
          component: ExternalFormOptions,
          meta: {
            externalFormEdit: true
          }
        }
      ]
    },
    {
      path: "/plans",
      name: "plans",
      component: Plans
    },
    {
      path: "/dashboard",
      name: "dashboard",
      component: Dashboard,
      meta: {
        layout: "dashboard"
      }
    },
    {
      path: "/plans/:id/edit",
      name: "plan_edit",
      props: true,
      component: PlanUpdate
    },
    {
      path: "/plans/:id",
      name: "plan_show",
      redirect: {
        name: "plan_edit"
      }
    },
    {
      path: `/${i18n.t("routes.projects")}`,
      name: "projects",
      component: Projects,
      children: [
        {
          path: `/${i18n.t("routes.project")}/new`,
          name: "project_new",
          component: ProjectWizard,
          meta: {
            showModal: true
          }
        }
      ]
    },
    {
      path: "/projects_synchronize",
      name: "projects_synchronize",
      component: ProjectsSynchronize
    },
    {
      path: `/${i18n.t("routes.project")}/:id/edit`,
      name: "project_edit",
      component: ProjectUpdate,
      props: true,
      meta: {
        layout: "classic"
      }
    },
    {
      path: `/${i18n.t("routes.project")}/:id/assignments`,
      name: "project_assignments_edit",
      component: ProjectAssignments
    },
    {
      path: `/${i18n.t("routes.project")}/:id/statistics`,
      name: "project_statistics",
      component: ProjectStatistics,
      props: true
    },
    {
      path: `/${i18n.t("routes.project")}/:id/statistics/trackings`,
      name: "project_statistics_trackings",
      component: ProjectStatisticsTrackings,
      props: true
    },
    {
      path: `/${i18n.t("routes.project")}/:id/statistics/trackings/frame`,
      name: "project_statistics_trackings_frame",
      component: ProjectStatisticsTrackingsFrame,
      props: true,
      meta: {
        layout: "empty"
      }
    },
    {
      path: `/${i18n.t("routes.project")}/:id/assignement`,
      name: "project_edit_assignement",
      component: ProjectUpdateAssignement,
      props: true
    },
    {
      path: `/${i18n.t("routes.project")}/:id`,
      name: "project_show",
      redirect: {
        name: "project_leads"
      }
    },
    {
      path: `/${i18n.t("routes.project")}/:id/${i18n.t(
        "routes.project_leads"
      )}/`,
      name: "project_leads",
      component: ProjectLeads,
      props: true
    },
    {
      path: `/${i18n.t("routes.project")}/:id/${i18n.t(
        "routes.project_leads"
      )}/:lead_id/edit/:query?`,
      name: "project_lead_edit",
      component: ProjectLeadUpdate,
      props: true
    },
    {
      path: `/${i18n.t("routes.project")}/:id/${i18n.t(
        "routes.project_leads"
      )}/:lead_id/statistics`,
      name: "project_lead_stats",
      component: ProjectLeadDetailedStatistics,
      props: true
    },
    {
      path: `/${i18n.t("routes.project_leads")}/:lead_id/statistics/frame`,
      name: "project_lead_detailed_stats",
      component: ProjectLeadDetailedStatisticsFrame,
      props: true,
      meta: {
        layout: "empty"
      }
    },
    {
      path: `/${i18n.t("routes.project_leads")}/:id/leads/new`,
      name: "project_lead_new",
      component: ProjectLeadCreate,
      props: true
    },
    {
      path: "/organizations",
      name: "organizations",
      component: Organizations
    },
    {
      path: "/contacts",
      name: "contacts",
      component: Contacts,
      props: route => {
        return { ...route.params };
      },
      meta: {
        layout: "classic",
        createSegment: false
      }
    },
    {
      path: "/companies",
      name: "companies",
      component: Companies,
      meta: {
        layout: "classic",
        createSegment: false
      }
    },
    {
      path: "/company/:id",
      name: "company",
      props: true,
      component: Company,
      meta: {
        layout: "classic",
        createSegment: false
      }
    },
    {
      path: "/segments",
      name: "segments",
      component: Segments,
      meta: {
        layout: "classic"
      }
    },
    {
      path: "/segments/:id/edit",
      name: "segment_edit",
      props: true,
      component: SegmentEdit,
      meta: {
        layout: "classic"
      }
    },
    {
      path: "/contact/:contactId",
      name: "contacts_edit",
      props: true,
      component: Contact,
      meta: {
        layout: "classic"
      }
    },
    {
      path: "/organization/:id/edit",
      name: "organization_edit",
      props: true,
      component: OrganizationUpdate
    },
    {
      path: "/organization/new",
      name: "organization_new",
      component: OrganizationUpdate
    },
    {
      path: "/contacts",
      name: "segment_new",
      component: Contacts,
      meta: {
        layout: "classic",
        createSegment: true
      }
    },
    {
      path: "/lead_masks",
      name: "lead_masks",
      component: LeadMasks
    },
    {
      path: "/lead_mask/:id/",
      name: "lead_mask_edit",
      component: LeadMaskUpdate,
      props: true,
      meta: {
        layout: "classic"
      },
      children: [
        {
          path: "edit",
          name: "lead_mask_editor",
          component: LeadMaskEditor
        },
        {
          path: "options",
          name: "lead_mask_options",
          component: LeadMaskOptions
        }
      ]
    },
    {
      path: "/lead_mask/new",
      name: "lead_mask_new",
      component: LeadMaskUpdate,
      meta: {
        layout: "classic"
      }
    },
    {
      path: "/email_templates",
      name: "email_templates",
      props: true,
      component: EmailTemplates
    },
    {
      path: "/email_templates/new",
      name: "email_template_new",
      props: true,
      component: EmailTemplateUpdate
    },
    {
      path: "/email_templates/:id/edit",
      name: "email_template_edit",
      props: true,
      component: EmailTemplateUpdate
    },
    {
      path: "/statistics",
      name: "statistics",
      component: Statistics
    },
    {
      path: "/content_manager",
      name: "content_manager",
      component: ContentManager
    },
    {
      path: "/users",
      name: "users",
      component: Users
    },
    {
      path: "/user/:id?/edit",
      name: "user_edit",
      component: UserUpdate,
      props: true
    },
    {
      path: "/user/:id?/profile",
      name: "user_profile",
      component: UserProfile,
      props: true
    },
    {
      path: "/unauthorized",
      name: "unauthorized",
      component: Unauthorized
    },
    {
      path: "/workflows",
      name: "workflows",
      component: Workflows
    },
    {
      path: "/workflow/:id/edit",
      name: "workflow_edit",
      props: true,
      component: WorkflowUpdate
    }
  ]
});

Vue.use(Router);
Vue.use(Meta);

export default router;



// WEBPACK FOOTER //
// ./src/config/router.js